import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@staff-app/ui';
import type { DeviceRmaRequest, RmaReturnStatus } from '@staff-app/shared-types';
import {
  useCreateRmaRestockingInvoice,
  useRmaStripeCustomer,
  useSetDeviceRmaReturnReceived,
} from '../hooks/useRma';

/** The states an operator can put a row into, in the order they occur. */
const CHOICES: Array<{ value: RmaReturnStatus; label: string; hint: string }> = [
  {
    value: 'AWAITING',
    label: 'Not received yet',
    hint: 'The faulty unit has not come back. Clears any receipt already recorded.',
  },
  {
    value: 'RECEIVED_TESTING',
    label: 'Received, testing',
    hint: 'The unit is back and being tested. Move it on once it is known to be complete or missing parts.',
  },
  {
    value: 'RECEIVED',
    label: 'Received, complete',
    hint: 'Everything came back — device and all accessories.',
  },
  {
    value: 'RECEIVED_INCOMPLETE',
    label: 'Received, missing parts',
    hint: 'Something was absent. Missing items are deducted, so say what.',
  },
];

/**
 * RmaReturnDialog — record whether the faulty unit came back.
 *
 * THE ONE PLACE THIS IS SET. It used to be a pair of buttons inside the eligibility dialog, which
 * put two controls for one fact on two different screens — the same "which one won?" problem that
 * kept the verification stamp off the detail drawer. Receipt is now recorded here and nowhere else,
 * reached from the Return column that displays it.
 *
 * It is a genuinely separate event from eligibility, not just a separate screen: eligibility is
 * decided before a replacement ships, receipt is observed weeks later, usually by someone else in
 * the warehouse. Recording one must never restamp the other.
 *
 * "Received with missing parts" is its own outcome rather than a footnote on Received, because
 * missing items are DEDUCTED — manual-replacement.md enumerates what must come back (stick, remote,
 * HDMI adapter, power adapter) — so it is a different financial result.
 *
 * "What was missing?" appears only on that branch and Save is refused while it is blank. The field
 * was built once, reverted with migration 026, dropped by 027 and reinstated by 030 — the ledger is
 * forward-only, so a re-added column takes a new number rather than the old file.
 */
export function RmaReturnDialog({
  row,
  onClose,
}: {
  row: DeviceRmaRequest;
  onClose: () => void;
}) {
  const [setReturned, { loading }] = useSetDeviceRmaReturnReceived();
  const [lookupCustomer, { data: cust, loading: looking, error: lookupError }] =
    useRmaStripeCustomer();
  const [raiseFee, { loading: raising }] = useCreateRmaRestockingInvoice();
  const [stripeId, setStripeId] = useState('');
  const [feeComment, setFeeComment] = useState('');
  const [feeError, setFeeError] = useState<string | null>(null);
  // What testing showed, and anything worth saying about it. '' = nothing recorded — which is a
  // different thing from NOT_TESTED, where somebody looked and decided not to.
  const [tested, setTested] = useState<string>(row.returnTestedOutcome ?? '');
  const [testNotes, setTestNotes] = useState(row.returnTestNotes ?? '');
  // The row as it came BACK from raising a fee. Holding it is what lets the dialog stay open and
  // show the outcome — closing on success would hide the one link the operator needs next.
  const [raised, setRaised] = useState<DeviceRmaRequest | null>(null);

  const resolved = cust?.rmaStripeCustomer;
  // Every one of these must hold before the button is live. A charge is the one thing here that
  // reaches a customer, so the gate is deliberately unforgiving.
  const canCharge =
    !raising &&
    Boolean(resolved) &&
    resolved?.deleted === false &&
    feeComment.trim().length > 0 &&
    !row.restockingInvoiceId;

  const chargeFee = async () => {
    setFeeError(null);
    try {
      if (!resolved) return; // unreachable while canCharge gates the button; not an assertion
      const res = await raiseFee({
        variables: {
          submissionId: row.submissionId,
          stripeCustomerId: resolved.id,
          comment: feeComment,
        },
      });
      // Deliberately NOT closing. The draft has to be finalized by hand or the customer never
      // receives it, so the link and that warning are the point of the moment — dismissing the
      // dialog would send the operator hunting for what they just made.
      setRaised(res.data?.createDeviceRmaRestockingInvoice ?? null);
    } catch (e) {
      setFeeError((e as Error).message);
    }
  };

  // The charge to report: what we just raised, or what was already on the row.
  const charge = raised ?? (row.restockingInvoiceId ? row : null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<RmaReturnStatus>(
    (row.returnStatus as RmaReturnStatus) ?? 'AWAITING',
  );
  const [missing, setMissing] = useState(row.returnMissingItems ?? '');

  const needsMissing = choice === 'RECEIVED_INCOMPLETE';
  // Required on that branch and nowhere else. "Received with missing parts" is a financial claim —
  // missing items are deducted — and a deduction with no stated basis is one nobody can defend when
  // the customer disputes it.
  const canSave = !loading && (!needsMissing || missing.trim().length > 0);

  const save = async () => {
    setError(null);
    try {
      await setReturned({
        variables: {
          submissionId: row.submissionId,
          // AWAITING is the CLEAR, expressed as a null condition on the wire.
          condition: choice === 'AWAITING' ? null : choice,
          // Forced null off that branch, so correcting to "complete" clears the old list rather
          // than smuggling it through.
          missingItems: needsMissing ? missing : null,
          // '' clears, same partial contract as the rest. The server also wipes both when the
          // receipt itself is cleared — a verdict about a unit we no longer say came back would
          // sit there justifying a fee for a return that never arrived.
          testedOutcome: tested,
          testNotes,
        },
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* SAME SHAPE AS THE FULFILMENT DIALOG (requester, 2026-09-22): same width, bounded at 85vh,
          body scrolls, decision buttons pinned. The two are opened from adjacent columns of the
          same row and are read as one workflow, so a narrower box that clipped instead of scrolling
          made them feel like different tools. This one has grown a testing-outcome section and the
          whole restocking-fee flow, and was overflowing on a laptop with no way to reach Save. */}
      <DialogContent
        className="flex max-h-[85vh] max-w-3xl flex-col"
        data-testid="rma-return-dialog"
      >
        <DialogHeader>
          <DialogTitle>{'Return status'}</DialogTitle>
        </DialogHeader>

        {/* Only the body scrolls — the same split as the fulfilment dialog, and for the same
            reason: Save must not be something you have to hunt for below the fee section.
            `-mr-2 pr-2` keeps the scrollbar beside the content rather than over it. */}
        <div className="-mr-2 flex flex-col gap-4 overflow-y-auto pr-2">
          <p className="text-sm text-muted-foreground">
            {`${row.deviceModel} · ${row.deviceCount ?? '—'} unit(s) · ${row.customerEmail}`}
          </p>

          <RadioGroup
            value={choice}
            onValueChange={(v) => setChoice(v as RmaReturnStatus)}
            className="flex flex-col gap-3"
          >
            {CHOICES.map((c) => (
              <div key={c.value} className="flex items-start gap-2">
                <RadioGroupItem
                  value={c.value}
                  id={`rma-return-${c.value}`}
                  data-testid={`rma-return-choice-${c.value}`}
                  className="mt-1"
                />
                <div>
                  <Label htmlFor={`rma-return-${c.value}`} className="cursor-pointer">
                    {c.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{c.hint}</p>
                </div>
              </div>
            ))}
          </RadioGroup>

          {needsMissing ? (
            <div>
              <Label htmlFor="rma-missing">{'What was missing?'}</Label>
              <textarea
                id="rma-missing"
                value={missing}
                onChange={(e) => setMissing(e.target.value)}
                rows={3}
                placeholder="e.g. remote and power adapter absent; HDMI adapter present"
                className="mt-1 w-full rounded-md border bg-background p-2 text-sm"
                data-testid="rma-missing-items"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {'Required — missing items are deducted, and this is what that deduction is justified from.'}
              </p>
            </div>
          ) : null}

          {row.returnedAt ? (
            <p className="text-xs text-muted-foreground" data-testid="rma-return-existing">
              {`Currently recorded by ${row.returnedBy ?? 'unknown'}.`}
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" data-testid="rma-return-error">
              {error}
            </p>
          ) : null}

          {/* TESTING OUTCOME — directly above the fee, because it is what justifies it (requester,
              2026-09-22). The restocking fee is for a device returned in working order, which the
              customer acknowledged when they filed; until now staff-app charged $50/$110/$220 with
              the finding behind it written down nowhere. A charge can be disputed months later, and
              "we tested it and it worked" needs to be a stored fact with a name against it.

              Only once something has come back: a verdict on a unit that has not arrived is not a
              thing anybody can have. */}
          {choice !== 'AWAITING' ? (
            <div className="flex flex-col gap-2 border-t pt-4" data-testid="rma-test-outcome">
              <h3 className="text-sm font-semibold">{'Testing outcome'}</h3>
              <p className="text-xs text-muted-foreground">
                {'Does the unit actually work? This is what a restocking fee rests on, so record it before charging one.'}
              </p>

              <div className="flex flex-wrap gap-2">
                {/* NOT_TESTED is offered as an ANSWER, not left as the blank: a unit destroyed in
                    transit, or a model the bench cannot power, is a decision somebody made — and it
                    reads differently from "nobody has looked yet" when a fee is questioned. */}
                {[
                  { value: 'WORKS', label: 'Works — no fault found' },
                  { value: 'FAULTY', label: 'Faulty — fault confirmed' },
                  { value: 'NOT_TESTED', label: 'Not tested' },
                ].map((o) => (
                  <label
                    key={o.value}
                    className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                      tested === o.value ? 'border-primary bg-muted' : 'hover:border-muted-foreground/70'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rma-tested"
                      className="sr-only"
                      checked={tested === o.value}
                      onChange={() => setTested(o.value)}
                      data-testid={`rma-tested-${o.value}`}
                    />
                    {o.label}
                  </label>
                ))}
                {tested ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTested('')}
                    data-testid="rma-tested-clear"
                  >
                    {'Clear'}
                  </Button>
                ) : null}
              </div>

              <div>
                <Label htmlFor="rma-test-notes" className="text-xs">
                  {'Notes (optional)'}
                </Label>
                <textarea
                  id="rma-test-notes"
                  value={testNotes}
                  onChange={(e) => setTestNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. booted fine on the bench, played a playlist for 20 minutes, no fault reproduced"
                  className="mt-1 w-full rounded-md border bg-background p-2 text-sm"
                  data-testid="rma-test-notes"
                />
                {/* Optional on purpose (requester): the verdict stands alone. Requiring prose
                    produces "tested, fine" on every row, which looks like evidence and is not. */}
                <p className="mt-1 text-xs text-muted-foreground">
                  {'Optional — the verdict above is the record; add notes when there is something worth saying.'}
                </p>
              </div>

              {row.returnTestedBy ? (
                <p className="text-xs text-muted-foreground" data-testid="rma-tested-stamp">
                  {`Recorded by ${row.returnTestedBy}${
                    row.returnTestedAt ? ` on ${new Date(row.returnTestedAt).toLocaleString()}` : ''
                  }`}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* THE RESTOCKING FEE — the only thing on this dialog that reaches a customer.
              Deliberately below the status, and deliberately not one click: the operator resolves a
              Stripe customer, SEES who it is, writes down why, and only then can charge. */}
          <div className="flex flex-col gap-3 border-t pt-4">
            <div>
              <h3 className="text-sm font-semibold">{'Restocking fee'}</h3>
              <p className="text-xs text-muted-foreground">
                {row.deviceModel === 'UNKNOWN'
                  ? 'No fee is defined for an unidentified device — the fee is per-model, so identify it first.'
                  : `${row.deviceModel} — a draft invoice is raised in Stripe. A human finalizes it there; nothing is emailed from here.`}
              </p>
            </div>

            {charge ? (
              // The created draft, with a link straight to it — the same shape the Billing
              // Console's hardware-invoice outcome uses, including its warning, because the hazard
              // is identical: a draft nobody finalizes is a fee the customer never sees.
              <div className="flex flex-col gap-2" data-testid="rma-fee-already">
                <p
                  className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
                  data-testid="rma-fee-draft-warning"
                >
                  {'⚠ This stays a DRAFT until someone finalizes it in Stripe. Do it by hand, or the customer never receives it.'}
                </p>
                <p className="text-xs text-muted-foreground" data-testid="rma-fee-summary">
                  {[
                    charge.restockingFeeMinor != null
                      ? `$${(charge.restockingFeeMinor / 100).toFixed(2)}`
                      : 'draft',
                    charge.restockingInvoiceId,
                    `raised by ${charge.restockingInvoicedBy ?? 'unknown'}`,
                  ]
                    .filter(Boolean)
                    .join(' — ')}
                </p>
                {charge.restockingInvoiceUrl ? (
                  <a
                    href={charge.restockingInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-primary underline underline-offset-2"
                    data-testid="rma-fee-invoice-link"
                  >
                    {'Open the draft invoice in Stripe →'}
                  </a>
                ) : (
                  // Rows charged before migration 029 captured the url. The id is still copyable.
                  <p className="font-mono text-xs text-muted-foreground">
                    {charge.restockingInvoiceId}
                  </p>
                )}
              </div>
            ) : row.deviceModel === 'UNKNOWN' ? null : (
              <>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label htmlFor="rma-stripe-id">{'Stripe customer'}</Label>
                    <Input
                      id="rma-stripe-id"
                      value={stripeId}
                      onChange={(e) => setStripeId(e.target.value)}
                      placeholder="cus_ABC123"
                      className="mt-1 font-mono"
                      data-testid="rma-fee-stripe-id"
                    />
                  </div>
                  <Button
                    variant="outline"
                    disabled={looking || !stripeId.trim()}
                    onClick={() =>
                      void lookupCustomer({ variables: { stripeCustomerId: stripeId.trim() } })
                    }
                    data-testid="rma-fee-lookup"
                  >
                    {looking ? 'Checking…' : 'Check'}
                  </Button>
                </div>

                {/* The confirm step. An operator approving a bare id is not approving anything. */}
                {lookupError ? (
                  <p className="text-sm text-destructive" data-testid="rma-fee-lookup-error">
                    {lookupError.message}
                  </p>
                ) : resolved ? (
                  <p
                    className={`text-sm${resolved.deleted ? ' text-destructive' : ''}`}
                    data-testid="rma-fee-resolved"
                  >
                    {resolved.deleted
                      ? `${resolved.id} was DELETED in Stripe — pick a different customer.`
                      : `${resolved.name ?? '(no name)'} · ${resolved.email ?? '(no email)'}`}
                  </p>
                ) : null}

                <div>
                  <Label htmlFor="rma-fee-comment">{'Why is this fee being charged?'}</Label>
                  <textarea
                    id="rma-fee-comment"
                    value={feeComment}
                    onChange={(e) => setFeeComment(e.target.value)}
                    rows={2}
                    placeholder="e.g. tested fine on the bench, fault could not be reproduced"
                    className="mt-1 w-full rounded-md border bg-background p-2 text-sm"
                    data-testid="rma-fee-comment"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {'Required — there is no approver on this path, so this is the whole record of why.'}
                  </p>
                </div>

                <Button
                  variant="destructive"
                  disabled={!canCharge}
                  onClick={() => void chargeFee()}
                  data-testid="rma-fee-charge"
                >
                  {raising ? 'Raising…' : 'Create restocking fee invoice'}
                </Button>
                {feeError ? (
                  <p className="text-sm text-destructive" data-testid="rma-fee-error">
                    {feeError}
                  </p>
                ) : null}
              </>
            )}
          </div>

        </div>

        {/* Outside the scroll area, with the buttons — a refusal that scrolled away while the
            operator was reading the form would look like nothing happened. */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={onClose} data-testid="rma-return-cancel">
            {'Cancel'}
          </Button>
          <Button disabled={!canSave} onClick={() => void save()} data-testid="rma-return-save">
            {'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
